import React, { useEffect, useState } from 'react';
import { Box, Fade, Typography, keyframes } from '@mui/material';
import Logo from './Logo';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

// ============================================================================
// Overlay de chargement global BLOBTIC — pont impératif pour blobticService
// Montage : <BlobticLoadingHost /> une seule fois dans App.tsx
// ============================================================================

const SHOW_DELAY_MS = 220;

let pendingCount = 0;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let hostListener: ((visible: boolean) => void) | null = null;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.04); opacity: 0.92; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

export const blobticLoading = {
    show(): void {
        pendingCount += 1;
        if (pendingCount === 1) {
            showTimer = setTimeout(() => {
                showTimer = null;
                if (pendingCount > 0) hostListener?.(true);
            }, SHOW_DELAY_MS);
        }
    },
    hide(): void {
        pendingCount = Math.max(0, pendingCount - 1);
        if (pendingCount === 0) {
            if (showTimer) {
                clearTimeout(showTimer);
                showTimer = null;
            }
            hostListener?.(false);
        }
    }
};

export const BlobticLoadingHost: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const { t } = useLanguage();
    const { user } = useAuth();

    useEffect(() => {
        hostListener = setVisible;
        return () => {
            hostListener = null;
            pendingCount = 0;
            if (showTimer) {
                clearTimeout(showTimer);
                showTimer = null;
            }
        };
    }, []);

    // Do not use MUI Backdrop/Modal: a second Modal on top of an open Dialog
    // leaves the dialog aria-hidden/inert (greyed, only Cancel usable).
    return (
        <Fade in={visible} timeout={280} unmountOnExit>
            <Box
                role="status"
                aria-live="polite"
                aria-busy={visible}
                sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: (theme) => theme.zIndex.modal + 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(15, 23, 42, 0.42)',
                    backdropFilter: 'blur(8px)',
                    pointerEvents: 'auto'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.5,
                        px: { xs: 3, sm: 4 },
                        py: { xs: 3.5, sm: 4 },
                        borderRadius: '24px',
                        background: 'linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
                        boxShadow: '0 28px 80px -20px rgba(15, 23, 42, 0.45)',
                        border: '1px solid rgba(255,255,255,0.75)',
                        minWidth: { xs: 260, sm: 300 }
                    }}
                >
                    <Box sx={{ position: 'relative', width: 96, height: 96 }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                border: '3px solid transparent',
                                borderTopColor: '#2563eb',
                                borderRightColor: '#60a5fa',
                                animation: `${spin} 1.1s linear infinite`
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 10,
                                borderRadius: '50%',
                                border: '2px solid transparent',
                                borderBottomColor: '#93c5fd',
                                borderLeftColor: '#3b82f6',
                                animation: `${spin} 1.6s linear infinite reverse`
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 22,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: `${pulse} 2s ease-in-out infinite`
                            }}
                        >
                            {user?.dosLogo ? (
                                <Box
                                    component="img"
                                    src={user.dosLogo}
                                    alt="Logo"
                                    sx={{ height: 44, width: 'auto', objectFit: 'contain', display: 'block' }}
                                />
                            ) : (
                                <Logo height={44} width="auto" />
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                background: 'linear-gradient(90deg, #1e3a8a, #2563eb, #1e3a8a)',
                                backgroundSize: '200% auto',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                animation: `${shimmer} 2.4s linear infinite`
                            }}
                        >
                            {t('common.loadingServer')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mt: 0.75, color: 'text.secondary', fontWeight: 500 }}
                        >
                            {t('common.loadingServerHint')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Fade>
    );
};
